<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { AIR_ONLY, TOTAL_COST, TOUR, CRUISE, OPENJAW, TOTAL_SELLING_PRICE } from '@/constants';
import { getTextVariant } from '@/utils/stringUtils';
import DataTable from 'primevue/datatable';
import TableColumn from 'primevue/column';

interface Props {
  detailPrice?: any;
  quoteType?: string;
  indexOption: number;
}

const props = defineProps<Props>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();

const dataPricingTotal = computed(() => handleFormatPricing());

const staticColumnsHotel = [
  { field: 'roomCategory', header: t('book_form.room_category') },
  { field: 'totalRooms', header: t('book_form.booking_price_summary.total_rooms') },
  { field: 'totalPax', header: t('book_form.booking_price_summary.total_pax') },
  { field: 'totalSellingPrice', header: t('book_form.booking_price_summary.total_selling_price') },
  { field: 'totalCost', header: t('book_form.booking_price_summary.total_cost') }
];

const staticColumnsFlight = [
  { field: 'airClass', header: t('common.class') },
  { field: 'totalPax', header: t('book_form.booking_price_summary.total_pax') },
  { field: 'totalSellingPrice', header: t('book_form.booking_price_summary.total_selling_price') },
  { field: 'totalCost', header: t('book_form.booking_price_summary.total_cost') }
];

const staticColumnsCruise = [
  { field: 'cruiseService', header: t('book_form.booking_price_summary.service') },
  { field: 'totalRoomCabin', header: t('book_form.booking_price_summary.total_rooms_cabins') },
  { field: 'totalPax', header: t('book_form.booking_price_summary.total_pax') },
  { field: 'totalSellingPrice', header: t('book_form.booking_price_summary.total_selling_price') },
  { field: 'totalCost', header: t('book_form.booking_price_summary.total_cost') }
];

const handleColumn = () => {
  switch (props.quoteType) {
    case AIR_ONLY:
      return staticColumnsFlight;
    case CRUISE:
      return staticColumnsCruise;
    default:
      return staticColumnsHotel;
  }
};

const handleAirText = (detailPrice: any) => {
  let text = t(`advance_quote.form.${detailPrice.itinerary.toLowerCase()}`);
  if (detailPrice.itinerary === OPENJAW) {
    text = `${text}: ${detailPrice.gateCode}-${detailPrice.destCode} / ${detailPrice.returnFromCode}-${detailPrice.returnToCode}`;
  } else {
    text = `${text}: ${detailPrice.gateCode}-${detailPrice.destCode}`;
  }
  return text.toUpperCase();
};

const handleLeadText = () => {
  switch (props.quoteType) {
    case AIR_ONLY:
      return handleAirText(props.detailPrice);
    case TOUR: {
      const label = t('report.tour_name');
      const tourName = getTextVariant(currentLanguage, props.detailPrice?.tourNameEn, props.detailPrice?.tourNameFr);
      return label + ': ' + tourName;
    }
    case CRUISE:
      return `${t('common.option')} ${props.indexOption + 1}`;
    default:
      return `${t('common.hotel')}: ${props.detailPrice.hotel}`;
  }
};

const formatFlightClass = (flightInfos: any) => {
  return flightInfos.map((flight: any) => {
    const fareBasis = flight.fareBasis ? ` - ${flight.fareBasis}` : '';
    return {
      ...flight,
      airClass: `${flight.airClass}${fareBasis}`
    };
  });
};

const removeEmptyRow = (rows: any) => {
  return rows.filter((row: any) => row.totalPax);
};

const handleFormatPricing = () => {
  const isCruise = props.quoteType === CRUISE;
  const fileTotalDetail = props.detailPrice?.fileTotalDetail;
  const lastItem = isCruise ? fileTotalDetail?.[fileTotalDetail.length - 1] : null;

  const itemTotalPrice = {
    roomCategory: t('common.total'),
    airClass: t('common.total'),
    cruiseService: t('common.total'),
    totalSellingPrice: isCruise ? lastItem?.totalSellingPrice : props.detailPrice?.totalValueSellingPrice,
    totalCost: isCruise ? lastItem?.totalCost : props.detailPrice?.totalValueCost,
    key: TOTAL_COST
  };

  switch (props.quoteType) {
    case AIR_ONLY:
      return [...removeEmptyRow(formatFlightClass(props.detailPrice.flightInfos)), itemTotalPrice];
    case CRUISE:
      return [...removeEmptyRow(props.detailPrice.fileTotalDetail), itemTotalPrice];
    default:
      return [...removeEmptyRow(props.detailPrice.roomsCategoryInfo), itemTotalPrice];
  }
};

const displayDataTable = (data: any, field: any) => {
  const value = data[field];

  if (field === TOTAL_COST) {
    if (props.quoteType === CRUISE) {
      return value ? (Number(value) || 0).toFixed(2) : value;
    }
    return (Number(value) || 0).toFixed(2);
  }

  if (field === TOTAL_SELLING_PRICE) {
    return (Number(value) || 0).toFixed(2);
  }

  return value;
};
</script>
<template>
  <div>
    <div>
      <div v-if="dataPricingTotal.length > 1" class="flex justify-content-between">
        <div class="pricing-total">
          <div class="lead-pricing">{{ handleLeadText() }}</div>
          <div>
            <DataTable :value="dataPricingTotal" class="w-full" editMode="row" showGridlines>
              <TableColumn v-for="column of handleColumn()" :key="column.field" :field="column.field" class="relative h-3rem">
                <template #header>
                  <span>
                    {{ column.header }}
                  </span>
                </template>
                <template #body="{ data, field }">
                  <div :class="[data.key === TOTAL_COST ? 'p-3 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                    {{ field === 'cruiseService' && data[field] !== $t('common.total') ? getTextVariant(currentLanguage, data?.cruiseServiceEn, data?.cruiseServiceFr) : displayDataTable(data, field) }}
                  </div>
                </template>
              </TableColumn>
            </DataTable>
          </div>
        </div>
        <div v-if="quoteType !== CRUISE" class="flex">
          <p>{{ $t('book_form.booking_price_summary.gross_margin') }}</p>
          <div class="ml-4">
            <p>{{ (Number(detailPrice.grossMargin) || 0).toFixed(2) }}</p>
            <p>{{ detailPrice.marginPercent }} %</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
